import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import { Plus, Users, Radio, Pause, Play, Edit3, Trash2, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * Admin component to view all profiles and create new ones
 */
const ProfileListAdmin = ({ onSelectProfile, onCreateNew, onEditProfile, onManageUsers, isCmsAdmin = false }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingProfile, setDeletingProfile] = useState(null); // { id, name }
  const [confirmInput, setConfirmInput] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch all profiles
  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use admin RPC to get all profiles (bypasses RLS via SECURITY DEFINER)
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error: err } = await supabase
        .rpc('get_all_profiles_for_admin', {
          p_user_email: user?.email?.toLowerCase() || ''
        });

      if (err) throw err;

      // Get detailed user info for each profile using RPC
      const profilesWithUsers = await Promise.all(
        (data || []).map(async (profile) => {
          try {
            const { data: users, error: usersErr } = await supabase
              .rpc('get_profile_users', { p_profile_id: profile.id });
            
            if (usersErr) {
              console.warn(`Failed to get users for ${profile.org_name}:`, usersErr);
              return {
                ...profile,
                users: []
              };
            }
            
            return {
              ...profile,
              users: users || []
            };
          } catch (userErr) {
            console.warn(`Error getting users for ${profile.org_name}:`, userErr);
            return {
              ...profile,
              users: []
            };
          }
        })
      );

      setProfiles(profilesWithUsers);
    } catch (err) {
      console.error('Error fetching profiles:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // Delete profile (CMS admin only)
  const handleDeleteProfile = async () => {
    if (!deletingProfile || confirmInput !== deletingProfile.name) return;
    setDeleteLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: result, error: err } = await supabase.rpc('delete_signal_profile', {
        p_profile_id: deletingProfile.id,
        p_admin_email: user?.email?.toLowerCase() || ''
      });
      if (err) throw err;
      if (result?.error) throw new Error(result.error);
      setDeletingProfile(null);
      setConfirmInput('');
      fetchProfiles();
    } catch (err) {
      console.error('Error deleting profile:', err);
      alert('Failed to delete profile: ' + err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Toggle profile active status
  const handleToggleActive = async (profileId, currentStatus) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: result, error: err } = await supabase.rpc('toggle_profile_active', {
        p_profile_id: profileId,
        p_user_email: user?.email?.toLowerCase() || ''
      });

      if (err) throw err;
      if (result?.error) throw new Error(result.error);
      
      // Refresh list
      fetchProfiles();
    } catch (err) {
      console.error('Error toggling profile:', err);
      alert('Failed to toggle profile: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Loading profiles...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Radio className="w-5 h-5 text-primary-600" />
            All Signal Profiles
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage client profiles and create new ones
          </p>
        </div>
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Profile
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-900">{profiles.length}</div>
          <div className="text-sm text-gray-600">Total Profiles</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">
            {profiles.filter(p => p.is_active).length}
          </div>
          <div className="text-sm text-gray-600">Active</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-600">
            {profiles.filter(p => !p.is_active).length}
          </div>
          <div className="text-sm text-gray-600">Paused</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-primary-600">
            {profiles.reduce((sum, p) => sum + (p.user_count || 0), 0)}
          </div>
          <div className="text-sm text-gray-600">Total Users</div>
        </div>
      </div>

      {/* Profile list */}
      {profiles.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <Radio className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No profiles yet</h3>
          <p className="text-gray-600 text-sm mb-4">
            Create your first signal profile to start monitoring
          </p>
          <button
            onClick={onCreateNew}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create First Profile
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {profiles.map((profile) => (
            <motion.div
              key={profile.id}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                {/* Profile info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {profile.org_name}
                    </h3>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                        profile.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          profile.is_active ? 'bg-green-500' : 'bg-gray-400'
                        }`}
                      />
                      {profile.is_active ? 'Active' : 'Paused'}
                    </span>
                    {profile.auto_approve && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        Auto-approve
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="text-gray-500">Industry</div>
                      <div className="font-medium text-gray-900">
                        {profile.org_industry || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Owner</div>
                      <div className="font-medium text-gray-900 truncate">
                        {profile.user_email}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Users</div>
                      <div className="font-medium text-gray-900 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {profile.user_count}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Approved/Rejected</div>
                      <div className="font-medium text-gray-900">
                        {profile.approved_count || 0} / {profile.rejected_count || 0}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-gray-500">
                    Created: {new Date(profile.created_at).toLocaleDateString()}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                  <button
                    onClick={() => handleToggleActive(profile.id, profile.is_active)}
                    className={`p-2 rounded-lg transition-colors ${
                      profile.is_active
                        ? 'text-yellow-600 hover:bg-yellow-50'
                        : 'text-green-600 hover:bg-green-50'
                    }`}
                    title={profile.is_active ? 'Pause' : 'Resume'}
                  >
                    {profile.is_active ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>

                  {onManageUsers && (
                    <button
                      onClick={() => onManageUsers(profile.id)}
                      className="p-2 text-gray-500 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                      title="Manage Users"
                    >
                      <Users className="w-4 h-4" />
                    </button>
                  )}

                  {onEditProfile && (
                    <button
                      onClick={() => onEditProfile(profile.id)}
                      className="p-2 text-gray-500 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                      title="Edit Profile"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  )}

                  {isCmsAdmin && (
                    <button
                      onClick={() => {
                        if (deletingProfile?.id === profile.id) {
                          setDeletingProfile(null);
                          setConfirmInput('');
                        } else {
                          setDeletingProfile({ id: profile.id, name: profile.org_name });
                          setConfirmInput('');
                        }
                      }}
                      className={`p-2 rounded-lg transition-colors ${
                        deletingProfile?.id === profile.id
                          ? 'text-red-600 bg-red-50'
                          : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                      }`}
                      title="Delete Profile"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}

                  <button
                    onClick={() => onSelectProfile(profile.id)}
                    className="px-3 py-2 bg-primary-50 text-primary-700 hover:bg-primary-100 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    View Signals
                  </button>
                </div>
              </div>

              {/* ─── Danger delete confirmation ─── */}
              {isCmsAdmin && deletingProfile?.id === profile.id && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 pt-4 border-t-2 border-red-200"
                >
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="p-2 bg-red-100 rounded-lg shrink-0">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-red-900 text-sm">Delete profile permanently?</h4>
                        <p className="text-red-700 text-xs mt-1 leading-relaxed">
                          This will permanently delete <strong>all signals, sources, and user access</strong> for
                          <strong> {profile.org_name}</strong>. Users themselves are not deleted — only their
                          access to this profile is removed. This action <strong>cannot be undone</strong>.
                        </p>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-red-800 mb-1">
                        Type <span className="font-bold">{profile.org_name}</span> to confirm deletion:
                      </label>
                      <input
                        type="text"
                        value={confirmInput}
                        onChange={(e) => setConfirmInput(e.target.value)}
                        onPaste={(e) => e.preventDefault()}
                        placeholder={profile.org_name}
                        className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:ring-2 focus:ring-red-400 focus:border-transparent bg-white"
                        autoFocus
                      />
                    </div>
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        type="button"
                        onClick={() => { setDeletingProfile(null); setConfirmInput(''); }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleDeleteProfile}
                        disabled={confirmInput !== profile.org_name || deleteLoading}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                      >
                        {deleteLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                        Delete Forever
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Users list (if any) */}
              {profile.users && profile.users.length > 1 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="text-xs font-medium text-gray-500 mb-1">Shared with:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {profile.users
                      .filter(u => u.role !== 'owner')
                      .slice(0, 5)
                      .map((user) => (
                        <span
                          key={user.id}
                          className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs"
                        >
                          {user.user_email} ({user.role})
                        </span>
                      ))}
                    {profile.users.length > 6 && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">
                        +{profile.users.length - 6} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProfileListAdmin;
